import { supabase } from './client';
import { authService } from './auth';
import { Database } from '../../types/supabase';

type TaskInsert = Database['public']['Tables']['tasks']['Insert'];
type TaskUpdate = Database['public']['Tables']['tasks']['Update'];

export const tasksService = {
  async getTasks(profileId: string) {
    const { user } = await authService.getUser();
    if (!user) throw new Error('User not authenticated');

    // Tasks no longer carry profile_id directly. Filter through the parent
    // routine. The explicit `tasks_routine_id_fkey` hint disambiguates a
    // duplicate FK constraint that exists on this column (cleaned up in
    // migration `i6j7k8l9m0n1`).
    const { data, error } = await supabase
      .from('tasks')
      .select('*, routines!tasks_routine_id_fkey!inner(profile_id)')
      .eq('routines.profile_id', profileId)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('[tasksService.getTasks] Error:', error);
      throw error;
    }
    return data;
  },

  async createTask(task: TaskInsert) {
    const { data, error } = await supabase
      .from('tasks')
      .insert(task)
      .select()
      .single();
    
    if (error) {
      console.error('[tasksService.createTask] Error:', error);
      throw error;
    }
    return data;
  },

  async updateTask(id: string, updates: TaskUpdate) {
    const { data, error } = await supabase
      .from('tasks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('[tasksService.updateTask] Error:', error);
      throw error;
    }
    return data;
  },

  async deleteTask(id: string) {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', id);
    
    if (error) {
      console.error('[tasksService.deleteTask] Error:', error);
      throw error;
    }
  },

  async getRecentUniqueTasks(profileId?: string) {
    const { user } = await authService.getUser();
    if (!user) throw new Error('User not authenticated');

    // Reach profile + display_name via the parent routine. Filter by
    // routine.user_id so we never leak across users.
    let query = supabase
      .from('tasks')
      .select(
        'id, label, icon, color, start_time, end_time, routine_id, ' +
        'routines!tasks_routine_id_fkey!inner(user_id, profile_id, profiles!inner(display_name))'
      )
      .eq('routines.user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(200);

    if (profileId) {
      query = query.eq('routines.profile_id', profileId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('[tasksService.getRecentUniqueTasks] Error:', error);
      throw error;
    }

    const uniqueTasks = new Map<string, any>();
    data.forEach((t: any) => {
      const profileIdFromRoutine = t.routines?.profile_id;
      const displayName = t.routines?.profiles?.display_name;
      const key = `${profileIdFromRoutine}-${t.label}-${t.icon}-${t.color}-${t.start_time}-${t.end_time}`;
      if (!uniqueTasks.has(key)) {
        const { routines, ...taskData } = t;
        uniqueTasks.set(key, {
          ...taskData,
          profile_id: profileIdFromRoutine,
          profile_name: displayName,
        });
      }
    });

    return Array.from(uniqueTasks.values());
  },

  // Raw rows for the Task Library picker: predefined (system) tasks plus the
  // user's historical tasks. Dedup/duration shaping happens in the hook.
  async getTaskLibrary(userId: string) {
    const { data: predefined, error: predefinedError } = await supabase
      .from('predefined_routine_tasks')
      .select('label, icon, color, default_duration_minutes')
      .order('label');
    if (predefinedError) {
      console.error('[tasksService.getTaskLibrary] predefined error:', predefinedError);
      throw predefinedError;
    }

    const { data: history, error: historyError } = await supabase
      .from('tasks')
      .select('label, icon, color, start_time, end_time, profiles!inner(user_id)')
      .eq('profiles.user_id', userId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (historyError) {
      console.error('[tasksService.getTaskLibrary] history error:', historyError);
      throw historyError;
    }

    return { predefined: predefined ?? [], history: history ?? [] };
  }
};
